import { useEffect, useState } from 'react';
import {
  Sparkles, Mail, Loader2, TrendingUp, PieChart, Wallet, Scale,
  Clock, AlertCircle, Calendar, ListChecks, RefreshCw, Briefcase, X,
  Banknote, History, Slack, Send, CheckCircle2, XCircle, MinusCircle,
  Video,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';
import { useWorkspace } from '../../context/WorkspaceContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'trending-up': TrendingUp,
  'pie-chart': PieChart,
  'wallet': Wallet,
  'scale': Scale,
  'clock': Clock,
  'alert-circle': AlertCircle,
  'calendar': Calendar,
  'list-checks': ListChecks,
  'refresh-cw': RefreshCw,
  'briefcase': Briefcase,
  'banknote': Banknote,
  'video': Video,
};

const CASH_FLOW_WINDOWS = [
  { value: 'daily',    label: 'Today',         hint: 'Yesterday\'s posted + current pending' },
  { value: 'wtd',      label: 'Week to date',  hint: 'Monday → today' },
  { value: 'mtd',      label: 'Month to date', hint: '1st of month → today' },
  { value: 'last_30d', label: 'Last 30 days',  hint: 'Rolling 30-day window' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DOW = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

// Common US timezones — anything else falls back to a free-text input.
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

function formatHour12(h) {
  const ampm = h < 12 ? 'AM' : 'PM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${ampm}`;
}

export const BriefSettingsSheet = ({ open, onOpenChange, onSaved }) => {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [recommendedKeys, setRecommendedKeys] = useState([]);
  const [recommendCost, setRecommendCost] = useState(null);
  const [slackTesting, setSlackTesting] = useState(false);
  const [pref, setPref] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch preferences + catalog every time the sheet opens.
  useEffect(() => {
    if (!open || !wsId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await apiFetch(
          `${API_URL}/api/dashboard/command-center/brief/preferences?workspace_id=${wsId}`,
        );
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setPref(data.preferences);
        setCatalog(data.topic_catalog || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, wsId]);

  const update = (patch) => setPref((p) => ({ ...p, ...patch }));

  const toggleTopic = (key) => {
    setPref((p) => {
      const has = (p.topics || []).includes(key);
      const next = has ? p.topics.filter((k) => k !== key) : [...(p.topics || []), key];
      return { ...p, topics: next };
    });
  };

  const askAI = async () => {
    if (!wsId) return;
    setAiBusy(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/recommend-topics?workspace_id=${wsId}`,
        { method: 'POST' },
      );
      if (r.ok) {
        const data = await r.json();
        const recs = data.recommended || [];
        setRecommendedKeys(recs);
        // Convert dollar cost to credits (1 credit = $0.01). Sub-credit spends
        // round up to 1 so the user never sees a "0 credits" misleading display.
        const usd = Number(data.cost_usd || 0);
        const credits = usd <= 0 ? 0 : Math.max(1, Math.round(usd * 100));
        setRecommendCost({ usd, credits, from_llm: !!data.from_llm });
        // Auto-merge recommended into current selection (idempotent).
        setPref((p) => ({
          ...p,
          topics: Array.from(new Set([...(p.topics || []), ...recs])),
        }));
        toast.success(
          data.from_llm
            ? `Studio AI recommends ${recs.length} topics for this workspace.`
            : `Picked ${recs.length} recommended topics based on your data.`,
        );
      } else {
        toast.error('Could not get AI recommendations');
      }
    } catch (e) {
      toast.error('Could not get AI recommendations');
    } finally {
      setAiBusy(false);
    }
  };

  const testSlackWebhook = async () => {
    if (!wsId || !pref?.slack_webhook_url) {
      toast.error('Paste a Slack webhook URL first');
      return;
    }
    setSlackTesting(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/test-slack?workspace_id=${wsId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ webhook_url: pref.slack_webhook_url }),
        },
      );
      if (r.ok) {
        toast.success('Test message sent! Check your Slack channel.');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Slack rejected the webhook.');
      }
    } finally {
      setSlackTesting(false);
    }
  };

  const loadHistory = async () => {
    if (!wsId) return;
    setHistoryLoading(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/deliveries?workspace_id=${wsId}&limit=20`,
      );
      if (r.ok) {
        const data = await r.json();
        setHistory(data.items || []);
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, wsId]);

  const sendNow = async () => {
    if (!wsId) return;
    setSending(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/send-now?workspace_id=${wsId}`,
        { method: 'POST' },
      );
      if (r.ok) {
        toast.success('Brief sent! Check your email / Slack.');
        // Refresh history so the new row appears immediately.
        loadHistory();
        if (!historyOpen) setHistoryOpen(true);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not send the brief now.');
      }
    } finally {
      setSending(false);
    }
  };


  const save = async () => {
    if (!wsId || !pref) return;
    if (!pref.topics || pref.topics.length === 0) {
      toast.error('Pick at least one topic to brief on.');
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/dashboard/command-center/brief/preferences?workspace_id=${wsId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pref),
        },
      );
      if (r.ok) {
        toast.success('Brief preferences saved.');
        onSaved?.(pref);
        onOpenChange(false);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save preferences');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[520px] overflow-y-auto"
        data-testid="brief-settings-sheet"
      >
        <SheetHeader className="space-y-1">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-violet-600" />
            Customize your Brief
          </SheetTitle>
          <SheetDescription>
            Pick what you want briefed on and how often. Studio AI will tailor
            every recap to exactly these topics.
          </SheetDescription>
        </SheetHeader>

        {loading || !pref ? (
          <div className="py-16 flex items-center justify-center text-sm text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your preferences…
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Subscribed master toggle */}
            <div
              className="flex items-center justify-between p-3 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200/60 dark:border-violet-900/40"
              data-testid="brief-settings-subscribed-row"
            >
              <div>
                <div className="text-sm font-semibold">Email me this brief</div>
                <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
                  Sends to your account email for this workspace only.
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!!pref.subscribed}
                  onChange={(e) => update({ subscribed: e.target.checked })}
                  data-testid="brief-settings-subscribed"
                />
                <div className="w-10 h-6 bg-zinc-300 dark:bg-zinc-700 peer-checked:bg-violet-600 rounded-full transition-colors relative">
                  <span className={cn(
                    'absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform shadow',
                    pref.subscribed && 'translate-x-4',
                  )} />
                </div>
              </label>
            </div>

            {/* Cadence */}
            <section>
              <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700 dark:text-zinc-300">
                How often?
              </Label>
              <RadioGroup
                value={pref.cadence}
                onValueChange={(v) => update({ cadence: v })}
                className="grid grid-cols-3 gap-2 mt-2"
                data-testid="brief-settings-cadence"
              >
                {[
                  { v: 'daily', label: 'Daily' },
                  { v: 'weekly', label: 'Weekly' },
                  { v: 'monthly', label: 'Monthly' },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className={cn(
                      'flex items-center justify-center gap-2 p-3 rounded-lg ring-1 cursor-pointer transition-colors',
                      pref.cadence === opt.v
                        ? 'ring-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 font-semibold'
                        : 'ring-zinc-200 dark:ring-white/10 hover:bg-zinc-50 dark:hover:bg-zinc-900',
                    )}
                  >
                    <RadioGroupItem value={opt.v} className="sr-only" />
                    {opt.label}
                  </label>
                ))}
              </RadioGroup>
            </section>

            {/* Topics */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700 dark:text-zinc-300">
                  What to brief on?
                </Label>
                <div className="flex items-center gap-2">
                  {recommendCost && recommendCost.from_llm && (
                    <span
                      className="text-[10px] text-zinc-500 dark:text-zinc-400"
                      data-testid="brief-settings-recommend-cost"
                      title={`Marked-up LLM cost: $${recommendCost.usd.toFixed(4)}`}
                    >
                      ~{recommendCost.credits} {recommendCost.credits === 1 ? 'credit' : 'credits'}
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={askAI}
                    disabled={aiBusy}
                    className="h-7 text-[11px] border-violet-300 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                    data-testid="brief-settings-ai-recommend"
                  >
                    {aiBusy ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-3 h-3 mr-1" />
                    )}
                    Ask AI to recommend
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-zinc-400 dark:text-zinc-500 -mt-1 mb-3">
                AI recommendations cost a small amount of credits per click (~1 credit).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {catalog.map((t) => {
                  const Icon = ICON_MAP[t.icon] || TrendingUp;
                  const selected = (pref.topics || []).includes(t.key);
                  const recommended = recommendedKeys.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => toggleTopic(t.key)}
                      className={cn(
                        'group relative text-left rounded-lg p-3 ring-1 transition-all',
                        selected
                          ? 'ring-violet-500 bg-violet-50/60 dark:bg-violet-950/30'
                          : 'ring-zinc-200 dark:ring-white/10 hover:ring-zinc-300 dark:hover:ring-white/20',
                      )}
                      data-testid={`brief-topic-${t.key}`}
                    >
                      {recommended && (
                        <Badge className="absolute -top-2 -right-2 text-[9px] bg-violet-600 text-white px-1.5 h-4 shadow">
                          AI pick
                        </Badge>
                      )}
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={cn(
                          'w-3.5 h-3.5',
                          selected ? 'text-violet-600' : 'text-zinc-500',
                        )} />
                        <span className="text-sm font-medium">{t.label}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 dark:text-zinc-400 line-clamp-2">
                        {t.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Cash flow window — only relevant when the Plaid cash-flow topic is selected */}
            {(pref.topics || []).includes('cash_flow_plaid') && (
              <section
                className="space-y-2 rounded-xl border border-emerald-200/70 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20 p-3"
                data-testid="brief-settings-cashflow-window"
              >
                <Label className="text-xs uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                  <Banknote className="w-3.5 h-3.5" /> Cash flow window
                </Label>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
                  Which period should the bank cash flow section cover? Inflows, outflows,
                  posted &amp; pending — pulled live from your connected Plaid accounts.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CASH_FLOW_WINDOWS.map((opt) => {
                    const selected = (pref.cash_flow_window || 'daily') === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => update({ cash_flow_window: opt.value })}
                        className={cn(
                          'flex flex-col items-start text-left px-2.5 py-2 rounded-md ring-1 transition-colors',
                          selected
                            ? 'ring-emerald-500 bg-white dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-semibold'
                            : 'ring-zinc-200 dark:ring-white/10 hover:bg-white/60 dark:hover:bg-zinc-900',
                        )}
                        data-testid={`brief-cashflow-window-${opt.value}`}
                      >
                        <span className="text-xs">{opt.label}</span>
                        <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-normal mt-0.5 leading-tight">
                          {opt.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Delivery time */}
            <section className="space-y-3">
              <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700 dark:text-zinc-300">
                When to send?
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {pref.cadence === 'weekly' && (
                  <div>
                    <Label className="text-[11px] text-zinc-500 mb-1 block">Day of week</Label>
                    <Select
                      value={String(pref.delivery_day_of_week)}
                      onValueChange={(v) => update({ delivery_day_of_week: parseInt(v, 10) })}
                    >
                      <SelectTrigger data-testid="brief-settings-dow">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOW.map((d) => (
                          <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {pref.cadence === 'monthly' && (
                  <div>
                    <Label className="text-[11px] text-zinc-500 mb-1 block">Day of month</Label>
                    <Select
                      value={String(pref.delivery_day_of_month)}
                      onValueChange={(v) => update({ delivery_day_of_month: parseInt(v, 10) })}
                    >
                      <SelectTrigger data-testid="brief-settings-dom">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-[11px] text-zinc-500 mb-1 block">Time of day</Label>
                  <Select
                    value={String(pref.delivery_hour_local)}
                    onValueChange={(v) => update({ delivery_hour_local: parseInt(v, 10) })}
                  >
                    <SelectTrigger data-testid="brief-settings-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)}>{formatHour12(h)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] text-zinc-500 mb-1 block">Timezone</Label>
                  <Select
                    value={pref.delivery_timezone}
                    onValueChange={(v) => update({ delivery_timezone: v })}
                  >
                    <SelectTrigger data-testid="brief-settings-tz">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Slack delivery (optional) */}
            <section className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700 dark:text-zinc-300">
                Also send to Slack? <span className="text-zinc-400 normal-case font-normal">(optional)</span>
              </Label>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Paste an Incoming Webhook URL from Slack (Apps → Incoming Webhooks → Add).
                The same Brief will land in your channel on the cadence you chose.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="https://hooks.slack.com/services/..."
                  value={pref.slack_webhook_url || ''}
                  onChange={(e) => update({ slack_webhook_url: e.target.value })}
                  className="flex-1 font-mono text-xs"
                  data-testid="brief-settings-slack-url"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={testSlackWebhook}
                  disabled={slackTesting || !pref.slack_webhook_url}
                  data-testid="brief-settings-slack-test"
                >
                  {slackTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                </Button>
              </div>
            </section>

            {/* Delivery history */}
            <section className="space-y-2 rounded-xl border border-zinc-200/70 dark:border-white/10 p-3">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                data-testid="brief-settings-history-toggle"
              >
                <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-700 dark:text-zinc-300">
                  <History className="w-3.5 h-3.5" /> Delivery history
                </span>
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
                  {historyOpen ? 'Hide' : 'Show recent sends'}
                </span>
              </button>
              {historyOpen && (
                <div className="space-y-1.5" data-testid="brief-settings-history-list">
                  {historyLoading ? (
                    <div className="flex items-center gap-2 text-xs text-zinc-500 py-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 py-2">
                      No sends yet. Use <strong>Send now</strong> below to fire your first test.
                    </div>
                  ) : (
                    history.map((row) => {
                      const tone =
                        row.status === 'sent'    ? 'text-emerald-600 dark:text-emerald-400'
                      : row.status === 'failed'  ? 'text-rose-600 dark:text-rose-400'
                                                 : 'text-amber-600 dark:text-amber-400';
                      const StatusIcon =
                        row.status === 'sent'    ? CheckCircle2
                      : row.status === 'failed'  ? XCircle
                                                 : MinusCircle;
                      const ts = (() => {
                        try {
                          return new Date(row.sent_at).toLocaleString(undefined, {
                            month: 'short', day: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          });
                        } catch { return row.sent_at; }
                      })();
                      const cadenceLabel = (row.cadence || 'weekly').replace(/^./, (c) => c.toUpperCase());
                      return (
                        <div
                          key={row.delivery_id}
                          className="flex items-center gap-2 text-[11px] py-1 px-1.5 rounded-md hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                          data-testid={`brief-delivery-${row.delivery_id}`}
                        >
                          <StatusIcon className={cn('w-3.5 h-3.5 shrink-0', tone)} />
                          <span className={cn('font-medium capitalize', tone)}>
                            {row.status}
                          </span>
                          <span className="text-zinc-500 dark:text-zinc-400">·</span>
                          <span className="text-zinc-700 dark:text-zinc-300">{cadenceLabel}</span>
                          <span className="text-zinc-400 dark:text-zinc-500 truncate">
                            {row.period_key}
                          </span>
                          <span className="ml-auto flex items-center gap-1">
                            {(row.channels || []).includes('email') && (
                              <Mail className="w-3 h-3 text-violet-600" aria-label="Email channel" />
                            )}
                            {(row.channels || []).includes('slack') && (
                              <Slack className="w-3 h-3 text-emerald-600" aria-label="Slack channel" />
                            )}
                            <span className="text-zinc-400 dark:text-zinc-500 ml-1.5">{ts}</span>
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </section>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                variant="outline"
                onClick={sendNow}
                disabled={sending || saving}
                data-testid="brief-settings-send-now"
                className="border-emerald-300 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-1" /> Send now</>
                )}
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-500 text-white"
                data-testid="brief-settings-save"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                ) : (
                  'Save preferences'
                )}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
